import { translate } from 'components/Translation';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BodyS, BodySBold, Caption } from 'components/Typography/Typography';
import { makeStyles } from '@material-ui/core/styles';
import styles from 'assets/jss/material-dashboard-pro-react/views/prepaidLoyalty/paymentMethodTabStyle';
import { capitalize } from 'utils/converters/capitalize';
import { UsersService } from 'services/users/users';
import { CorpUserData } from 'utils/api/models/users/corpUserData';
import { InfoOutlined } from '@material-ui/icons';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import { SingleBillId } from 'utils/api/models/bills/singleBillId';
import CustomSearch, { ISearchElement } from 'components/CustomSearch/CustomSearch';
import { Subscription } from 'rxjs';
import { AccountsService } from 'services/accounts/accounts';
import { SingleBillingNumber } from 'utils/api/models/accounts/singleBillingNumber';
import { finalize } from 'rxjs/operators';

// @ts-ignore
const useStyles = makeStyles(styles);

type MasterNumberFormProps = {
	masterNumber: string;
	creditLimit?: string;
	setCreditLimit?: React.Dispatch<React.SetStateAction<string>>;
	setMasterNumber: (masterNumber: string) => void;
	setAccountNumber?: (accountNumber: string) => void;
	setAccountDetails?: (accountDetails: string) => void;
	setUserData?: (data: CorpUserData) => void;
	numbers: SingleBillingNumber[];
	setNumbers: React.Dispatch<React.SetStateAction<SingleBillingNumber[]>>;
	filteredBillingIds: SingleBillId[];
};

const MasterNumberForm = ({
	masterNumber,
	creditLimit,
	setCreditLimit,
	setMasterNumber,
	setAccountNumber,
	setAccountDetails,
	setUserData,
	numbers,
	setNumbers,
	filteredBillingIds,
}: MasterNumberFormProps) => {
	const [upAccountId, setUpAccountId] = useState<string>('');
	const [loadingNumbers, setLoadingNumbers] = useState<{ billingId: string; loading: boolean }[]>(
		filteredBillingIds.map(({ billingId }) => ({ billingId, loading: true }))
	);
	const loading = loadingNumbers.some(({ loading }) => loading);
	const isMounted = useRef(true);
	const classes = useStyles();

	const fetchNumbersForFilteredBillingIds = useCallback(() => {
		return new Promise((resolve: (value: Subscription[]) => void) => {
			const subscriptions: Subscription[] = [];
			setNumbers([]);
			for (const { billingId, accountId } of filteredBillingIds) {
				const subscription = AccountsService.getNumbersForBillingAccount(accountId, billingId)
					.pipe(
						finalize(() => {
							setLoadingNumbers((oldLoadingNumbers) =>
								oldLoadingNumbers.map((singleNumber) =>
									singleNumber.billingId === billingId
										? { ...singleNumber, loading: false }
										: singleNumber
								)
							);
						})
					)
					.subscribe(({ numbers }) => {
						setNumbers((prevNumbers) => [...prevNumbers, ...numbers]);
					});
				subscriptions.push(subscription);
			}
			resolve(subscriptions);
		});
	}, [filteredBillingIds, setNumbers]);

	useEffect(() => {
		const fetchPromise = fetchNumbersForFilteredBillingIds();

		return () => {
			fetchPromise.then((subscriptions: Subscription[]) => {
				for (const subscription of subscriptions) {
					subscription.unsubscribe();
				}
			});
		};
	}, [fetchNumbersForFilteredBillingIds]);

	const mappedBillingIdNumbers = useMemo(() => {
		return numbers.map(({ accountNum }) => ({
			id: accountNum,
			value: accountNum,
			content: accountNum,
		}));
	}, [numbers]);

	useEffect(() => {
		if (upAccountId) {
			UsersService.getCorpUserDepartmentDataForNumber(upAccountId).subscribe((data) => {
				if (isMounted.current && setUserData) {
					setUserData(data);
				}
			});
		}
	}, [upAccountId, setUserData]);

	const selectedSuggestion = useMemo(() => {
		return masterNumber
			? {
					id: masterNumber,
					value: masterNumber,
			  }
			: null;
	}, [masterNumber]);

	const onSuggestionClick = useCallback(
		(suggestion: ISearchElement | null) => {
			if (suggestion) {
				setMasterNumber(suggestion.value);
			} else {
				setMasterNumber('');
				if (setCreditLimit) {
					setCreditLimit('');
				}
			}
		},
		[setCreditLimit, setMasterNumber]
	);

	useEffect(() => {
		if (!!masterNumber && setAccountNumber) {
			let masterNumberObj = numbers.find((numberFromServer) => numberFromServer.accountNum === masterNumber);
			setAccountNumber(masterNumberObj?.bAccountId || '');
			if (setAccountDetails) {
				setAccountDetails(
					`${capitalize(masterNumberObj?.accountType)} - ${capitalize(masterNumberObj?.billingType)}`
				);
			}
			setUpAccountId(masterNumberObj?.upAccountId || '');
		}
	}, [masterNumber, numbers, setAccountNumber, setAccountDetails]);

	return (
		<div className={classes.masterNumberContainer}>
			<Caption color="secondary-light">
				{translate('prepaid.loyalty.add.new.services.payment.method.master.number.caption')}
			</Caption>
			<div className={classes.paymentMethodInputContainer}>
				<CustomSearch
					elements={mappedBillingIdNumbers}
					id="prepaidNumbersSearch"
					label={translate('prepaid.loyalty.add.new.services.payment.method.master.number.input.label')}
					elementSelected={onSuggestionClick}
					selectedElement={selectedSuggestion}
					loading={loading}
				/>
				<ConditionalRender
					show={!!creditLimit}
					onTrue={() => (
						<div className={classes.creditLimitInfo}>
							<InfoOutlined />
							<div className={classes.creditLimitInfoText}>
								<BodyS inline color="secondary-light">
									{translate('top.up.add.ons.paymentTab.credit.limit')}
								</BodyS>
								<BodySBold inline color="secondary-light">
									{translate('price.with.dot', creditLimit || '')}
								</BodySBold>
								<ConditionalRender
									show={creditLimit !== undefined && +creditLimit >= 250}
									onTrue={() => (
										<BodyS color="secondary-light">
											{translate('top.up.add.ons.paymentTab.account.manager')}
										</BodyS>
									)}
								/>
							</div>
						</div>
					)}
				/>
			</div>
		</div>
	);
};

export default MasterNumberForm;
